// Ficha de una comunicación: KPIs, datos del envío y a quién le llegó.
// Mientras se está enviando, se refresca cada 5 s.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Card, Descriptions, Input, Progress, Select, Spin, Table, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  SearchOutlined,
  StopOutlined,
  UserDeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import Comunicaciones, { mensajeError } from '../../../../service/Comunicaciones';
import { EncabezadoAdmin, TarjetaResumen, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import VistaPreviaCorreo from './VistaPreviaCorreo';
import { ESTADOS, ESTADOS_ENTREGA, ORIGENES, PLANTILLAS, fechaHora } from './textos';

const EN_CURSO = ['encolada', 'enviando'];

const OPCIONES_ENTREGA = [
  { value: 'todos', label: 'Todas' },
  ...Object.entries(ESTADOS_ENTREGA).map(([value, { label }]) => ({ value, label })),
];

export default function ComunicacionFicha() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'comunicaciones', 'editar');

  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [totalEntregas, setTotalEntregas] = useState(0);
  const [cargandoEntregas, setCargandoEntregas] = useState(false);
  const [filtro, setFiltro] = useState({ estado: 'todos', q: '', page: 1 });
  const [busqueda, setBusqueda] = useState('');
  const [previa, setPrevia] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setC(await Comunicaciones.obtener(id));
      setError(null);
    } catch (e) {
      setError(mensajeError(e, 'No se pudo cargar la comunicación'));
    }
  }, [id]);

  const cargarEntregas = useCallback(async () => {
    setCargandoEntregas(true);
    try {
      const r = await Comunicaciones.entregas(id, { ...filtro, limit: 50 });
      setEntregas(r?.data || []);
      setTotalEntregas(r?.pagination?.total || 0);
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudieron cargar las entregas'));
    } finally {
      setCargandoEntregas(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filtro]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (c && c.estado !== 'borrador') cargarEntregas(); }, [c?.estado, cargarEntregas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sondeo solo mientras el envío está en curso.
  const enCurso = EN_CURSO.includes(c?.estado);
  useEffect(() => {
    if (!enCurso) return undefined;
    const t = setInterval(() => { cargar(); cargarEntregas(); }, 5000);
    return () => clearInterval(t);
  }, [enCurso, cargar, cargarEntregas]);

  const verCorreo = async () => {
    try {
      setPrevia(await Comunicaciones.previsualizar({ asunto: c.asunto, plantilla: c.plantilla, datos: c.datos, bloques: c.bloques, segmento: c.segmento }));
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudo armar el correo'));
    }
  };

  const detener = () =>
    modal.confirm({
      title: '¿Detener el envío?',
      content: 'Los correos que ya salieron no se pueden recuperar. Lo que falta queda sin enviar.',
      okText: 'Detener',
      okType: 'danger',
      cancelText: 'Seguir enviando',
      onOk: async () => {
        try {
          const r = await Comunicaciones.cancelar(id);
          notify.success(`Envío detenido: ${r.yaEnviados} ya habían salido, se detuvieron ${r.detenidos}.`);
          cargar();
        } catch (e) {
          notify.error(mensajeError(e));
        }
      },
    });

  if (error) return <div className="p-6"><Alert type="error" showIcon message={error} /></div>;
  if (!c) return <div className="p-10 flex justify-center"><Spin /></div>;

  const total = c.totalDestinatarios || 0;
  const sinEntregar = (c.fallidos || 0) + (c.omitidos || 0);
  const pct = total ? Math.round(((c.enviados + sinEntregar) / total) * 100) : 0;

  const columnas = [
    { title: 'Nombre', dataIndex: 'nombre', render: (v) => v || <span className="text-gray-400">Sin nombre</span> },
    { title: 'Correo', dataIndex: 'correo', render: (v) => <span className="font-mono text-xs">{v}</span> },
    { title: 'Por qué', dataIndex: 'origen', render: (v) => <span className="text-sm text-gray-600">{ORIGENES[v] || v}</span> },
    {
      title: 'Estado',
      dataIndex: 'estado',
      render: (v, e) => (
        <div>
          <Tag color={ESTADOS_ENTREGA[v]?.color}>{ESTADOS_ENTREGA[v]?.label || v}</Tag>
          {e.motivo && v !== 'enviado' && <div className="text-xs text-gray-500 mt-0.5 max-w-xs truncate" title={e.motivo}>{e.motivo === 'cancelada' ? 'Envío detenido' : e.motivo}</div>}
        </div>
      ),
    },
    { title: 'Enviado', dataIndex: 'enviadoEn', render: (v) => <span className="text-sm">{fechaHora(v)}</span> },
  ];

  return (
    <div className="p-4 md:p-6">
      <button type="button" onClick={() => navigate('/admin/comunicaciones')} className="text-sm text-gray-500 hover:text-primary mb-2 inline-flex items-center gap-1">
        <ArrowLeftOutlined /> Comunicaciones
      </button>
      <EncabezadoAdmin titulo={c.asunto} subtitulo={c.publico}>
        <button type="button" className={claseBtnSecundario} onClick={verCorreo}><EyeOutlined /> Ver el correo</button>
        {puedeEditar && c.estado === 'borrador' && (
          <button type="button" className={claseBtnPrimario} onClick={() => navigate(`/admin/comunicaciones/${id}/editar`)}><EditOutlined /> Editar</button>
        )}
        {puedeEditar && (
          <button type="button" className={claseBtnSecundario} onClick={() => navigate(`/admin/comunicaciones/redactar?desde=${id}`)}><CopyOutlined /> Duplicar</button>
        )}
        {puedeEditar && enCurso && (
          <button type="button" className={`${claseBtnSecundario} !text-red-600 hover:!border-red-400`} onClick={detener}><StopOutlined /> Detener</button>
        )}
      </EncabezadoAdmin>

      {c.pausadaHasta && enCurso && new Date(c.pausadaHasta) > new Date() && (
        <Alert className="mb-4" type="warning" showIcon
          message={`Se agotó la cuota diaria del proveedor de correo. El envío sigue automáticamente desde ${fechaHora(c.pausadaHasta)}.`} />
      )}
      {c.motivoFallo && <Alert className="mb-4" type="error" showIcon message={c.motivoFallo} />}

      {c.estado !== 'borrador' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <TarjetaResumen icon={<MailOutlined />} label="Destinatarios" value={total.toLocaleString('es-CL')} />
            <TarjetaResumen icon={<CheckCircleOutlined />} label="Entregados" value={(c.enviados || 0).toLocaleString('es-CL')} />
            <TarjetaResumen icon={<WarningOutlined />} label="Sin entregar" value={sinEntregar.toLocaleString('es-CL')} />
            <TarjetaResumen icon={<UserDeleteOutlined />} label="Bajas desde este correo" value={(c.bajas || 0).toLocaleString('es-CL')} />
          </div>
          {enCurso && (
            <Card size="small" className="mb-4">
              <div className="text-sm text-gray-600 mb-1">Enviando… {pct}%</div>
              <Progress percent={pct} status="active" showInfo={false} />
            </Card>
          )}
        </>
      )}

      <Card size="small" className="mb-4">
        <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
          <Descriptions.Item label="Estado"><Tag color={ESTADOS[c.estado]?.color}>{ESTADOS[c.estado]?.label || c.estado}</Tag></Descriptions.Item>
          <Descriptions.Item label="Tipo">{PLANTILLAS[c.plantilla]?.label || c.plantilla}</Descriptions.Item>
          <Descriptions.Item label="Público">{c.publico}</Descriptions.Item>
          <Descriptions.Item label="Creada">{fechaHora(c.createdAt)} · {c.creadoPor}</Descriptions.Item>
          <Descriptions.Item label="Inicio del envío">{fechaHora(c.iniciadaEn)}</Descriptions.Item>
          <Descriptions.Item label="Término">{fechaHora(c.terminadaEn)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {c.estado !== 'borrador' && (
        <Card size="small" title="Entregas">
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre o correo"
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); if (!e.target.value) setFiltro({ ...filtro, q: '', page: 1 }); }}
              onPressEnter={() => setFiltro({ ...filtro, q: busqueda.trim(), page: 1 })}
              className="sm:max-w-sm"
            />
            <Select value={filtro.estado} options={OPCIONES_ENTREGA} onChange={(v) => setFiltro({ ...filtro, estado: v, page: 1 })} className="sm:w-48" />
          </div>
          <Table
            rowKey="_id"
            size="small"
            columns={columnas}
            dataSource={entregas}
            loading={cargandoEntregas && !entregas.length}
            scroll={{ x: 720 }}
            pagination={{
              current: filtro.page,
              pageSize: 50,
              total: totalEntregas,
              showSizeChanger: false,
              showTotal: (t) => `${t.toLocaleString('es-CL')} destinatarios`,
              onChange: (p) => setFiltro({ ...filtro, page: p }),
            }}
          />
        </Card>
      )}

      <VistaPreviaCorreo abierta={!!previa} onCerrar={() => setPrevia(null)} asunto={previa?.asunto} html={previa?.html} />
    </div>
  );
}
